import React, { useEffect, useState, useMemo } from "react";
import { Package, Plus, Search, AlertTriangle, Minus } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { TableSkeleton, CardGridSkeleton } from "@/components/LoadingSkeleton";
import { ImageStager } from "@/components/ImageStager";
import { authFetch } from "@/lib/auth";
import { uploadAttachment } from "@/lib/attachments";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import type { InventoryItem } from "@shared/types";
export function InventoryPage() {
  const isMobile = useIsMobile();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [addOpen, setAddOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fName, setFName] = useState("");
  const [fCat, setFCat] = useState("");
  const [fQty, setFQty] = useState("");
  const [fUnit, setFUnit] = useState("");
  const [fMin, setFMin] = useState("");
  const [fNotes, setFNotes] = useState("");
  // Staged image attached after the inventory item is created.
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [consumeId, setConsumeId] = useState<string | null>(null);
  const [consumeQty, setConsumeQty] = useState("");
  const [consumeReason, setConsumeReason] = useState("");
  const [consuming, setConsuming] = useState(false);
  const fetchItems = async () => {
    try {
      const data = await authFetch<{ items: InventoryItem[] }>("/api/inventory");
      setItems(data.items ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error("[Inventory]", msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchItems(); }, []);
  const categories = useMemo(() => {
    const cats = new Set(items.map((i) => i.category).filter(Boolean));
    return Array.from(cats).sort();
  }, [items]);
  const filtered = useMemo(() => {
    return items.filter((i) => {
      const ms = i.name.toLowerCase().includes(search.toLowerCase());
      const mc = catFilter === "all" || i.category === catFilter;
      return ms && mc;
    });
  }, [items, search, catFilter]);
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fName.trim()) { toast.error("Name is required"); return; }
    setSubmitting(true);
    try {
      const created = await authFetch<{ id: string }>("/api/inventory", {
        method: "POST",
        body: JSON.stringify({
          name: fName.trim(), category: fCat.trim(), quantity: parseInt(fQty) || 0,
          unit: fUnit.trim(), min_quantity: parseInt(fMin) || 0, notes: fNotes.trim(),
        }),
      });

      if (imageFile && created?.id) {
        try {
          await uploadAttachment(imageFile, {
            linked_entity_type: "inventory_item",
            linked_entity_id: created.id,
          });
        } catch (uploadErr) {
          toast.warning(
            `Saved, but image upload failed: ${(uploadErr as Error).message ?? "unknown error"}`,
          );
        }
      }

      toast.success("Item added");
      setFName(""); setFCat(""); setFQty(""); setFUnit(""); setFMin(""); setFNotes(""); setImageFile(null);
      setAddOpen(false);
      setLoading(true);
      fetchItems();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to add");
    } finally {
      setSubmitting(false);
    }
  };
  const handleConsume = async () => {
    if (!consumeId) return;
    const qty = parseInt(consumeQty);
    if (!qty || qty <= 0) { toast.error("Enter a valid quantity"); return; }
    setConsuming(true);
    try {
      const updated = await authFetch<InventoryItem>(`/api/inventory/${consumeId}/consume`, {
        method: "POST",
        body: JSON.stringify({ quantity: qty, reason: consumeReason.trim() }),
      });
      toast.success(`Consumed ${qty} units`);
      setItems((prev) => prev.map((i) => i.id === consumeId ? { ...i, quantity: updated.quantity, updated_at: updated.updated_at } : i));
      setConsumeId(null);
      setConsumeQty("");
      setConsumeReason("");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to consume");
    } finally {
      setConsuming(false);
    }
  };
  const consumeItem = items.find((i) => i.id === consumeId);
  const getProgressPercent = (item: InventoryItem) => {
    const max = Math.max(item.quantity, item.min_quantity * 2, 1);
    return Math.min((item.quantity / max) * 100, 100);
  };
  return (
    <>
      <PageHeader title="Inventory" breadcrumbs={[{ label: "Dashboard", href: "/" }, { label: "Inventory" }]}>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Plus className="h-4 w-4 mr-2" /> Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-2">
              <div className="space-y-2"><Label>Name *</Label><Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Cattle Feed" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Category</Label><Input value={fCat} onChange={(e) => setFCat(e.target.value)} placeholder="Feed" /></div>
                <div className="space-y-2"><Label>Unit</Label><Input value={fUnit} onChange={(e) => setFUnit(e.target.value)} placeholder="bags" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Quantity</Label><Input type="number" value={fQty} onChange={(e) => setFQty(e.target.value)} placeholder="0" /></div>
                <div className="space-y-2"><Label>Min Quantity</Label><Input type="number" value={fMin} onChange={(e) => setFMin(e.target.value)} placeholder="0" /></div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={fNotes} onChange={(e) => setFNotes(e.target.value)} rows={2} /></div>
              <div className="space-y-2">
                <Label>Image</Label>
                <ImageStager
                  file={imageFile}
                  onChange={setImageFile}
                  disabled={submitting}
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
                {submitting ? "Adding..." : "Add Item"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </PageHeader>
      {error && <div className="mb-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{error}</div>}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
        <div className="relative flex-1 w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search inventory..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {/* Consume Dialog */}
      <Dialog open={!!consumeId} onOpenChange={(open) => { if (!open) setConsumeId(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Consume {consumeItem?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-muted-foreground">Available: {consumeItem?.quantity ?? 0} {consumeItem?.unit}</p>
            <div className="space-y-2"><Label>Quantity to consume *</Label><Input type="number" value={consumeQty} onChange={(e) => setConsumeQty(e.target.value)} placeholder="0" min="1" max={String(consumeItem?.quantity ?? 0)} /></div>
            <div className="space-y-2"><Label>Reason</Label><Textarea value={consumeReason} onChange={(e) => setConsumeReason(e.target.value)} rows={2} placeholder="Why are you consuming this?" /></div>
            <Button onClick={handleConsume} disabled={consuming} className="w-full bg-ranch-gold hover:bg-ranch-gold/90 text-white">
              {consuming ? "Consuming..." : "Confirm Consume"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {loading ? (
        isMobile ? <CardGridSkeleton count={4} /> : <TableSkeleton rows={5} cols={5} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Package} title="No inventory items found" description={search || catFilter !== "all" ? "Try adjusting your filters." : "Add your first inventory item."} actionLabel={!search && catFilter === "all" ? "Add Item" : undefined} onAction={!search && catFilter === "all" ? () => setAddOpen(true) : undefined} />
      ) : isMobile ? (
        <div className="space-y-3">
          {filtered.map((item) => {
            const isLow = item.quantity < item.min_quantity;
            return (
              <Card key={item.id} className={cn("p-4", isLow && "border-l-4 border-l-destructive")}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">{item.name}</h3>
                    <p className="text-2xs text-muted-foreground">{item.category}</p>
                  </div>
                  {isLow && <Badge variant="destructive" className="text-2xs"><AlertTriangle className="h-3 w-3 mr-1" />Low</Badge>}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Progress value={getProgressPercent(item)} className="flex-1 h-2" />
                  <span className="text-sm font-medium whitespace-nowrap">{item.quantity} {item.unit}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setConsumeId(item.id)} className="w-full">
                  <Minus className="h-3 w-3 mr-1" /> Consume
                </Button>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Stock Level</TableHead>
                <TableHead className="text-right">Quantity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                const isLow = item.quantity < item.min_quantity;
                return (
                  <TableRow key={item.id} className={cn(isLow && "border-l-4 border-l-destructive")}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-muted-foreground">{item.category}</TableCell>
                    <TableCell className="w-40">
                      <Progress value={getProgressPercent(item)} className="h-2" />
                    </TableCell>
                    <TableCell className="text-right font-medium">{item.quantity} {item.unit}</TableCell>
                    <TableCell>
                      {isLow ? (
                        <Badge variant="destructive" className="text-2xs"><AlertTriangle className="h-3 w-3 mr-1" />Low Stock</Badge>
                      ) : (
                        <Badge variant="outline" className="text-2xs bg-moss/10 text-moss border-moss/30 dark:bg-moss-dark/15 dark:text-moss-dark dark:border-moss-dark/30">OK</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => setConsumeId(item.id)}>
                        <Minus className="h-3 w-3 mr-1" /> Consume
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  );
}
